import { useState, useEffect } from 'react';
import { Dropdown, Checkbox, Tooltip, Modal, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useNavigate } from 'react-router-dom';
import Heading from '../common/Heading';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_NODE_API_ENDPOINT } from '../utils';
import { Meta } from '../interfaces/apiMeta';
import VerismartAudienceListing from './VerismartAudienceListing';
import { sanitizeHtml } from '../utils/sanitize';
import { handleApiError } from '../utils/errorHandler';


interface Category {
    id: number;
    fieldId: number;
    categoryName: string;
    categoryDefinition: string;
    subCategoryOverlap: boolean;
    verismartCategoryName: string;
    subCategories: SubCategory[];
}

interface SubCategory {
    id: number;
    categoryId: number;
    subCategoryName: string;
    subCategoryDefinition: string;
    estimatedScaleMnUsers: number;
    estimatedCpmRupees: number;
    verismartSubCategoryName: string;
}


export const Audience = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();
    const [audienceName, setAudienceName] = useState('');
    const [selectedFilterTypes, setSelectedFilterTypes] = useState<string[]>([]);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [visibleDropdown, setVisibleDropdown] = useState<string | null>(null);
    const [audienceCategories, setAudienceCategories] = useState<Category[]>([]);

    const handleSubCategorySelect = (subCategory: string, categoryName: string) => {
        // console.log(subCategory, categoryName);

        const uniqueIdentifier = `${categoryName}:${subCategory}`;
        const newFilters = activeFilters.includes(uniqueIdentifier)
            ? activeFilters.filter(f => f !== uniqueIdentifier)
            : [...activeFilters, uniqueIdentifier];
        setActiveFilters(newFilters);

    };

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate(`/audience`)}>Audience</div>,
            },
        ]);
    }, [setBreadcrumbs]);

    const handleSubmit = () => {
        if (audienceName === "") {
            alert("Audience name is required")
            return
        }

        // Create the formatted array of objects based on active filters
        const formattedFilters: Array<{ [key: string]: { value: string[], mandatory: boolean } }> = [];

        // Group filters by category
        const categoryGroups = new Map<string, string[]>();

        // console.log(activeFilters, "[]");


        activeFilters.forEach(filter => {
            const [categoryName, subCategoryName] = filter.split(':');

            // Find the category in audienceCategories
            const category = audienceCategories.find(c => c.verismartCategoryName === categoryName);
            if (category) {
                // Find the subcategory to get its verismartSubCategoryName
                const subCategory = category.subCategories.find(sc => sc.subCategoryName === subCategoryName);
                if (subCategory) {
                    if (!categoryGroups.has(category.verismartCategoryName)) {
                        categoryGroups.set(category.verismartCategoryName, []);
                    }
                    categoryGroups.get(category.verismartCategoryName)?.push(subCategory.verismartSubCategoryName);
                }
            }
        });

        // Convert the Map to array of objects format
        categoryGroups.forEach((subCategories, categoryName) => {
            const categoryObject = {
                [categoryName]: {
                    value: subCategories,
                    mandatory: true
                }
            };
            formattedFilters.push(categoryObject);
        });

        // Filters are now managed in component state only
        // For persistence across page refreshes, consider using URL parameters
        setIsModalVisible(true);
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 0);
    };

    useEffect(() => {
        const fetchAudienceCategories = async () => {
            setLoading(true);
            try {
                const result = await coreAPIService.get<{ meta: Meta, data: Category[] }>(
                    CTW_NODE_API_ENDPOINT,
                    // "http://localhost:3001/integration/api",
                    `common/v1/get_audience_categories`,
                );

                if (result.meta.code === 200 && result.meta.success) {
                    setAudienceCategories(result.data);
                } else {
                    handleApiError(new Error(result.meta.message), 'audience-categories-fetch', 'Failed to retrieve categories');
                }
            } catch (error) {
                handleApiError(error, 'audience-categories-fetch', 'Error fetching audience categories');
            } finally {
                setLoading(false);
            }
        };

        fetchAudienceCategories();
    }, []);

    useEffect(() => {
        if (isModalVisible) {
            const timer = setTimeout(() => {
                // Group subcategories by their parent category
                const groupedFilters: { [key: string]: string[] } = {};
                activeFilters.forEach(filter => {
                    const [categoryName, subCategory] = filter.split(':');
                    if (!groupedFilters[categoryName]) {
                        groupedFilters[categoryName] = [];
                    }
                    groupedFilters[categoryName].push(subCategory);
                });

                // Format the filters parameter as category:{subcategory}

                // Get unique categories
                const categoriesParam = Object.keys(groupedFilters).join(',');

                navigate(`/calculatedAudience?filters=${encodeURIComponent(JSON.stringify(groupedFilters))}&categories=${encodeURIComponent(categoriesParam)}&name=${audienceName}`);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [isModalVisible, activeFilters]);

    const formattedDefinition = (str: string) => (
        str.replace(/<li>/g, '<li>• ') // Replace <li> with <li>- globally
    );

    return (
        <>
            <section className="px-10 py-2">
                <div className="bg-white shadow-md rounded-lg p-6">
                    {/* Audience Name Input */}
                    <Heading title={"Audience Estimator"} />
                    <div className="bg-gray-50 p-4">
                        <div className="mb-8">
                            <input
                                type="text"
                                placeholder="Enter Audience Name"
                                value={audienceName}
                                onChange={(e) => setAudienceName(e.target.value)}
                                className="w-full p-4 border rounded-lg text-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Filter Type Selection */}
                        <div className="mb-8 p-4 border rounded-lg">
                            <h3 className="font-semibold mb-4">Add Audience Type:</h3>
                            <div className="flex flex-wrap gap-2">
                                {audienceCategories.map(category => (
                                    <Tooltip
                                        key={category.categoryName}
                                        title={category.categoryDefinition}
                                        placement="top"
                                    >
                                        <span
                                            className={`inline-flex items-center cursor-pointer mr-2 mb-2 px-3 py-1 rounded-full transition-colors ${selectedFilterTypes.includes(category.categoryName)
                                                ? 'bg-blue-100 border border-blue-300 text-blue-800'
                                                : 'bg-gray-100 hover:bg-blue-100 text-gray-700'
                                                }`}
                                            onClick={() => {
                                                setSelectedFilterTypes(prev =>
                                                    prev.includes(category.categoryName)
                                                        ? prev.filter(n => n !== category.categoryName)
                                                        : [...prev, category.categoryName]
                                                );
                                            }}
                                        >
                                            <span className="font-medium text-sm">{category.categoryName}</span>
                                        </span>
                                    </Tooltip>
                                ))}
                            </div>
                        </div>

                        {/* Subcategory Dropdowns */}

                        {selectedFilterTypes.length ? <div className="space-y-4 border rounded-lg">
                            <div className="grid grid-cols-2 gap-8 p-4">
                                {selectedFilterTypes.map(filterType => {
                                    const category = audienceCategories.find(c => c.categoryName === filterType);
                                    const activeInCategory = activeFilters
                                        .filter(f => f.startsWith(`${category?.verismartCategoryName}:`))
                                        .map(f => f.split(':')[1]);

                                    return (
                                        <div key={filterType} className="relative">
                                            <Dropdown
                                                overlay={
                                                    <div className="grid grid-cols-2 gap-4 p-4 w-[600px] bg-gray-100 rounded-lg" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                        {category?.subCategories.map(sub => (
                                                            <div
                                                                key={sub.subCategoryName}
                                                                className={`p-3 border rounded-lg hover:bg-blue-50 cursor-pointer ${activeFilters.includes(`${category.verismartCategoryName}:${sub.subCategoryName}`)
                                                                    ? 'bg-blue-100 border-blue-300'
                                                                    : 'bg-white'
                                                                    }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSubCategorySelect(sub.subCategoryName, category.verismartCategoryName);
                                                                }}
                                                            >
                                                                <div className='flex gap-2'>
                                                                    <Checkbox
                                                                        checked={activeFilters.includes(`${category.verismartCategoryName}:${sub.subCategoryName}`)}
                                                                        className="mb-1"
                                                                    >
                                                                        <div className="font-medium">{sub.subCategoryName}</div>
                                                                    </Checkbox>
                                                                </div>
                                                                <div className="text-sm text-gray-500 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(formattedDefinition(sub.subCategoryDefinition)) }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                }
                                                placement="bottom"
                                                trigger={['click']}
                                                visible={visibleDropdown === filterType}
                                                onVisibleChange={(visible) => {
                                                    setVisibleDropdown(visible ? filterType : null);
                                                }}
                                            >
                                                <button className={`w-full p-3 border rounded-lg text-left transition-colors ${activeInCategory.length > 0
                                                    ? 'border-blue-300 text-blue-800'
                                                    : 'bg-white hover:border-blue-500'
                                                    }`}>
                                                    <div className='flex justify-between'>
                                                        <div>
                                                            <div className="font-medium">{category?.categoryName}</div>
                                                            <div className="text-sm text-gray-500">
                                                                {activeInCategory.join(', ') || 'Select sub-categories'}
                                                            </div>
                                                        </div>
                                                        <DownOutlined />
                                                    </div>
                                                </button>
                                            </Dropdown>
                                        </div>
                                    );
                                })}
                            </div>
                        </div> : <></>}
                    </div>

                    {/* Submit Button */}
                    <div className="mt-6 text-center">
                        <button
                            onClick={handleSubmit}
                            className="w-[100px] p-3 bg-[#1F344C] text-white rounded-lg hover:bg-blue-600 transition-colors"
                            disabled={activeFilters.length < 1}
                        >
                            Submit
                        </button>
                    </div>

                    {/* Modal for displaying loader and selected filters */}
                    <Modal
                        title="Calculating Audience size"
                        open={isModalVisible}
                        footer={null}
                        // onCancel={() => setIsModalVisible(false)}
                        closable={false}
                        centered
                    >
                        <div className="flex flex-col items-center">
                            {loading ? (
                                <div>Loading...</div>
                            ) : (
                                <>
                                    {/* <div>
                                <div>Selected Filters:</div>
                                <div>{activeFilters.join(', ')}</div>
                            </div> */}
                                    <Spin className="m-4" />
                                </>
                            )}
                        </div>
                    </Modal>
                </div>
            </section>
            <VerismartAudienceListing />
        </>
    );
};

// // Reusable Dropdown Filter Component
// const DropdownFilter = ({ label, options, onSelect }: {
//     label: string;
//     options: SubCategory[];
//     onSelect: (selected: string[]) => void
// }) => {
//     const [selected, setSelected] = useState<string[]>([]);

//     const menu = (
//         <div className="p-2 bg-white border rounded-lg shadow-lg">
//             <Checkbox.Group
//                 value={selected}
//                 onChange={(values) => {
//                     setSelected(values);
//                     onSelect(values);
//                 }}
//                 className="space-y-2"
//             >
//                 {options.map(option => (
//                     <div key={option.id} className="p-2 hover:bg-gray-50 rounded">
//                         <Tooltip
//                             title={option.subCategoryDefinition}
//                             placement="top"
//                         //   disableInteractive
//                         >
//                             <Checkbox value={option.subCategoryName}>
//                                 <div>
//                                     <div>{option.subCategoryName}</div>
//                                     <div className="text-sm text-gray-500">
//                                         {option.estimatedScaleMnUsers}M users ·
//                                         ₹{option.estimatedCpmRupees} CPM
//                                     </div>
//                                 </div>
//                             </Checkbox>
//                         </Tooltip>
//                     </div>
//                 ))}
//             </Checkbox.Group>
//         </div>
//     );

//     return (
//         <Dropdown
//             overlay={menu}
//             trigger={['click']}
//         >
//             <button className="w-full p-3 bg-white border rounded-lg hover:border-blue-500">
//                 <div className="text-left">
//                     <div className="font-medium">{label}</div>
//                     <div className="text-sm text-gray-500">
//                         {selected.length > 0 ? selected.join(', ') : 'Select options'}
//                     </div>
//                 </div>
//             </button>
//         </Dropdown>
//     );
// };
